import { DateTimeFormatOptions } from "luxon";

import {
  PRO_NOTES_UNIT_PRICES,
  COUNTRIES_CURRENCY,
  MAX_TOTAL_UPLOAD_DURATION_LIMIT_MS,
} from "./constants";

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';
type ResponseType = 'json' | 'text';
type COUNTRY_ISO = "CA" | "GB" | "US" | "XX";

/**
 *
 * @param {string} endpoint - The endpoint to hit, to be appended to the end of the server URL
 * @param {string} method - The request method, eg. "GET", "POST", etc
 * @param {string} body - Optional, the body of the request
 * @returns - The response
 */
export async function makeMPServerRequest(endpoint: string, method: HttpMethod, responseType: ResponseType, body?: any) {
    const myHeaders = new Headers();
    myHeaders.append(
        "Authorization",
        `Bearer ${JSON.parse(localStorage.getItem("tokenMP")!).accessToken}`
    );

    myHeaders.append("Content-Type", "application/json");

    const requestOptions = {
        method: method,
        headers: myHeaders,
        redirect: "follow",
        ...(body && { body: body }), //optionally include
    };

    try {
        const response = await fetch(
            process.env.VUE_APP_MP_SERVER_API_URL + endpoint,
            requestOptions
        );

        // Check if the HTTP status code indicates an error
        if (!response.ok) {
          let errorMessage = `HTTP error! Status: ${response.status}`;
          try {
            const errorBody = await response.json();
            if (errorBody && errorBody.message) {
              errorMessage += ` Message: ${errorBody.message}`;
            }
          } catch {
            // parsing failed, do nothing, keep default message
          }
          throw new Error(errorMessage);
        }

        try {
            if (responseType === 'text') {
                return await response.text();
            }
            else {
                return await response.json();
            }
        } catch (error: unknown) {
            // Handle parsing errors
            if (error instanceof Error) {
                throw new Error(
                    "Error parsing response from server: " + error.message
                );
            }
            else {
                console.error("An unexpected error occurred:", error);
                throw new Error("An unexpected error occurred");
            }
        }
    } catch (error) {
        // Handle network or fetching errors
        if (error instanceof Error) {
            throw new Error(
                "Network error when calling the API: " + error.message
            );
        }
        else {
            console.error("An unexpected error occurred:", error);
            throw new Error("An unexpected error occurred");
        }
    }
}

/**
 * Calculates a pro notes order in cents based on duration and user country
 * @param durationMilliseconds 
 * @param userCountryIso
 * @returns 
 */
export function calculateOrderPriceInCents(durationMilliseconds: number, userCountryISO: COUNTRY_ISO) {
    const durationMinutes = Math.ceil(durationMilliseconds / 1000 / 60);

    // Cost is calculated by the minutes, rounded up
    return (
        PRO_NOTES_UNIT_PRICES[userCountryISO]["48hours"] *
        durationMinutes
    );
}

export function getFormattedPrice(subtotal: number, userCountryISO: COUNTRY_ISO) {
    const currencySymbol = COUNTRIES_CURRENCY[userCountryISO].symbol
    const currencyAbbreviation = COUNTRIES_CURRENCY[userCountryISO].abbreviation;

    return currencySymbol + subtotal + " " + currencyAbbreviation
}

export function limitCharacter(
  event: KeyboardEvent,
  currentString: string,
  showToastMessage?: Function,
) {
  if (!event || !event.target) {
    return;
  }

  const key = event.key;
  // Ignore backspace (8) and delete (46)
  //        left (37) and right (39)
  if (
    currentString.length >= 50 &&
    key != "Backspace" &&
    key != "Delete" &&
    key != "ArrowRight" &&
    key != "ArrowLeft"
  ) {
    if (showToastMessage) {
      showToastMessage("Title can be up to 50 characters");
    }
    event.preventDefault();
  }
}

export async function getUsedTime(hours = false) {
  const response = await makeMPServerRequest("self/free-hours-remaining", "GET", "json", null);
  const usedMilliseconds = response.millis;

  if (hours) {
    return Math.floor(usedMilliseconds / 3600000);
  }

  return usedMilliseconds;
}

export async function getFreeRemainingTime(hours = false) {
  const usedMilliseconds = await getUsedTime();
  const timeRemaining = MAX_TOTAL_UPLOAD_DURATION_LIMIT_MS - usedMilliseconds;
  if (hours) {
    return Math.floor(timeRemaining / 3600000);
  }

  return timeRemaining;
}

/**
 * Slices a Blob object into an ArrayBuffer and returns the sliced portion as a Promise.
 * 
 * @param {Blob} blob - The Blob object that will be sliced.
 * @param {number} start - The start index (inclusive) of the portion to slice.
 * @param {number} end - The end index (inclusive) of the portion to slice.
 * @returns {Promise<ArrayBuffer>} A Promise that resolves with the sliced ArrayBuffer.
 * 
 * @throws {Error} Throws an error if there is an issue reading the Blob or during the slice operation.
 */
export function sliceBlobUsingArrayBuffer(blob: Blob, start: number, end: number): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = function () {
      if (reader.readyState === FileReader.DONE) {
        const buffer = reader.result as ArrayBuffer;
        try {
          const slicedBuffer = buffer.slice(start, end + 1);
          resolve(slicedBuffer);
        } catch (error) {
          if (error instanceof TypeError) {
            reject(new Error(`TypeError while slicing ArrayBuffer: ${error}`));
          } else {
            reject(error); 
          }
        }
      }
    };

    reader.onerror = function (error) {
      reject(`Error reading the blob: ${error}`);
    };

    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Checks if the client supports slicing a `Blob` using `ArrayBuffer`.
 * Attempts to slice a `Blob` into a 5MB chunk and returns `true` if successful.
 * If an error occurs (e.g., the client does not support `ArrayBuffer` slicing),
 * it logs an error message and returns `false`.
 *
 * @param {Blob} blob - The `Blob` object to check for slicing support.
 * @returns {Promise<boolean>} A promise that resolves to `true` if the slicing is supported, or `false` if not.
 */
export async function supportSliceBlobUsingArrayBuffer(blob: Blob): Promise<boolean> {
  try {
    await sliceBlobUsingArrayBuffer(blob, 0, 5 * 1024 * 1024);
    return true;
  } catch (error) {
    console.error("The client does not support array buffer slice: disabling multiupload", error);
    return false;
  }
}

export function formatDateToCurrentLocale(dateString: string, withTime: boolean) {
    try {
        const dateObj = new Date(dateString)

        // Ensure valid date object
        if (isNaN(dateObj.getTime())) {
            throw new Error("Invalid date format")
        }

        if(withTime) {
            const options:DateTimeFormatOptions = {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            };
            return dateObj.toLocaleString(undefined, options)
        } else {
            const options:DateTimeFormatOptions = {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            };
            return dateObj.toLocaleDateString(undefined, options)
        }
    } catch (error) {
        console.error("Error formatting date:", error)
        return "Invalid date format"
    }
}